<template>
  <div v-if="!readonly">
    <el-form ref="formRef" :model="order" label-position="top" require-asterisk-position="right">
      <div class="flex-container">
        <el-form-item :label="$t('components.product-parameter.balcony.orientation-type')">
          <el-radio-group id="orientation" v-model="order.orientationType" size="large" class="radio-Button">
            <el-radio-button
              v-for="orientation in Object.values(OrientationType)"
              :key="orientation"
              :value="orientation"
            >
              <el-tooltip placement="top">
                <template #content>
                  <img
                    class="parameter-img"
                    :src="getImageSrc(orientation)"
                    alt="Orientation"
                  />
                </template>
                <span>{{ $t(`enum.orientationType.${orientation}`) }}</span>
              </el-tooltip>
            </el-radio-button>
          </el-radio-group>
        </el-form-item>

        <el-form-item :label="$t('components.product-parameter.balcony.carrying-out')">
          <el-select
            v-model="order.fenceType"
            class="select-component"
            :placeholder="Object.values(FenceType).at(0) ? $t(`enum.fenceType.${Object.values(FenceType).at(0)}`) : ''"
          >
            <el-option
              v-for="type in Object.values(FenceType)"
              :key="type"
              :value="type"
              :label="$t(`enum.fenceType.${type}`)"
            >
              <div class="option-label">
                <span>{{ $t(`enum.fenceType.${type}`) }}</span>
                <img
                  class="option-image"
                  :src="getImageSrc(type)"
                  alt="Fence Type"
                  style="width: 45px; margin-left: 8px"
                />
              </div>
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item :label="$t('components.product-parameter.balcony.paneel-type')">
          <el-radio-group id="panelType" v-model="order.paneelType" size="large" class="radio-Button">
            <el-radio-button
              v-for="paneel in Object.values(PaneelType)"
              :key="paneel"
              :value="paneel"
            >
              <el-tooltip placement="top">
                <template #content>
                  <img
                    class="parameter-img"
                    :src="getImageSrc(paneel)"
                    alt="Paneel Type"
                  />
                </template>
                <span>{{ $t(`enum.paneelType.${paneel}`) }}</span>
              </el-tooltip>
            </el-radio-button>
          </el-radio-group>
        </el-form-item>

        <el-form-item :label="$t('components.product-parameter.balcony.mounting-type')">
          <el-radio-group id="mountingType" v-model="order.mountingType" size="large" class="radio-Button">
            <el-radio-button
              v-for="mounting in Object.values(MountingType)"
              :key="mounting"
              :value="mounting"
            >
              <el-tooltip placement="top">
                <template #content>
                  <img
                    class="parameter-img"
                    :src="getImageSrc(mounting)"
                    alt="Mounting Type"
                  />
                </template>
                <span>{{ $t(`enum.mountingType.${mounting}`) }}</span>
              </el-tooltip>
            </el-radio-button>
          </el-radio-group>
        </el-form-item>
      </div>

      <el-form-item :label="$t('components.order-form.width')">
        <el-slider v-model.number="order.calfCircumferenceWithoutDeduction" :min="2000" :max="20000" />
        <el-input-number
          v-model.number="order.calfCircumferenceWithoutDeduction"
          class="number-input-component"
          :min="2000"
        />
      </el-form-item>

      <el-form-item :label="$t('components.order-form.height')">
        <el-slider v-model.number="order.heightWithoutDeduction" :min="0" :max="20000" disabled />
        <el-input-number
          v-model.number="order.heightWithoutDeduction"
          class="number-input-component"
          :min="0"
          disabled
        />
      </el-form-item>

      <el-form-item
        v-show="order.fenceType === FenceType.lLeft"
        :label="$t('components.order-form.depth-left')"
      >
        <el-slider v-model.number="order.depth" :min="2000" :max="20000" />
        <el-input-number
          v-model.number="order.depth"
          class="number-input-component"
          :min="2000"
        />
      </el-form-item>

      <el-form-item
        v-show="order.fenceType === FenceType.lRight"
        :label="$t('components.order-form.depth-right')"
      >
        <el-slider v-model.number="order.depthRight" :min="2000" :max="20000" />
        <el-input-number
          v-model.number="order.depthRight"
          class="number-input-component"
          :min="2000"
        />
      </el-form-item>

      <el-form-item
        v-show="order.fenceType === FenceType.uForm"
        :label="$t('components.order-form.depth')"
      >
        <el-slider v-model.number="order.depth" :min="2000" :max="20000" />
        <el-input-number
          v-model.number="order.depth"
          class="number-input-component"
          :min="2000"
        />
      </el-form-item>
    </el-form>
  </div>
  <div v-else>
    <div class="row1">
      <label class="col-sm-6 form-label text-secondary">
        {{ $t('components.product-parameter.balcony.orientation-type') }}
      </label>
      <label class="col-sm-6 form-label">
        {{ $t(`enum.orientationType.${order?.orientationType}`) }}
      </label>
    </div>

    <div class="row1">
      <label class="col-sm-6 form-label text-secondary">
        {{ $t('components.product-parameter.balcony.carrying-out') }}
      </label>
      <label class="col-sm-6 form-label">
        {{ $t(`enum.fenceType.${order?.fenceType}`) }}
      </label>
    </div>

    <div class="row1">
      <label class="col-sm-6 form-label text-secondary">
        {{ $t('components.product-parameter.balcony.paneel-type') }}
      </label>
      <label class="col-sm-6 form-label">
        {{ $t(`enum.paneelType.${order?.paneelType}`) }}
      </label>
    </div>

    <div class="row1">
      <label class="col-sm-6 form-label text-secondary">
        {{ $t('components.product-parameter.balcony.mounting-type') }}
      </label>
      <label class="col-sm-6 form-label">
        {{ $t(`enum.mountingType.${order?.mountingType}`) }}
      </label>
    </div>

    <div class="row1">
      <label class="col-sm-6 form-label text-secondary">
        {{ $t('components.order-form.width') }}
      </label>
      <label class="col-sm-6 form-label">
        {{ order?.calfCircumferenceWithoutDeduction }}
      </label>
    </div>

    <div class="row1">
      <label class="col-sm-6 form-label text-secondary">
        {{ $t('components.order-form.height') }}
      </label>
      <label class="col-sm-6 form-label">
        {{ order?.heightWithoutDeduction }}
      </label>
    </div>

    <div class="row1" v-if="order?.fenceType === FenceType.lLeft">
      <label class="col-sm-6 form-label text-secondary">
        {{ $t('components.order-form.depth-left') }}
      </label>
      <label class="col-sm-6 form-label">
        {{ order?.depth }}
      </label>
    </div>

    <div class="row1" v-if="order?.fenceType === FenceType.lRight">
      <label class="col-sm-6 form-label text-secondary">
        {{ $t('components.order-form.depth-right') }}
      </label>
      <label class="col-sm-6 form-label">
        {{ order?.depthRight }}
      </label>
    </div>

    <div class="row1" v-if="order?.fenceType === FenceType.uForm">
      <label class="col-sm-6 form-label text-secondary">
        {{ $t('components.order-form.depth') }}
      </label>
      <label class="col-sm-6 form-label">
        {{ order?.depth }}
      </label>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Emit, Prop, Ref } from 'vue-facing-decorator';
// Make sure correct Enums are imported
import { FenceType, MountingType, OrientationType, PaneelType } from '@/types/enum/order';
import { ElForm } from 'element-plus';
import { OrderData } from '@/types/interfaces/orderData';

@Component({
  components: {},
  emits: ['isValid', 'sizeChanged'], // Added 'isValid' based on your validateForm method
})
@Component
export default class ProductParamterFence extends Vue {
  @Prop() order!: OrderData;
  @Prop() product!: string;
  @Prop({ default: false }) readonly!: boolean;

  // Important: Ensure formRef is correctly typed if possible, or use 'any'/'unknown' if InstanceType causes issues
  @Ref('formRef') formRef!: InstanceType<typeof ElForm>; // Or: formRef!: typeof ElForm | null;

  // Expose Enums to the template
  FenceType = FenceType;
  MountingType = MountingType;
  PaneelType = PaneelType;
  OrientationType = OrientationType;

  mounted(): void {
    try {
      // Initialize defaults only if they aren't already set or if specifically needed for 'fence'
      // Check if product is 'fence' to apply fence-specific defaults
      if (this.product === 'fence') {
          // Set sensible defaults if properties might be undefined/null initially
          this.order.orientationType = this.order.orientationType ?? Object.values(OrientationType)[0];
          this.order.fenceType = this.order.fenceType ?? FenceType.straight; // Default to straight
          this.order.paneelType = this.order.paneelType ?? Object.values(PaneelType)[0];
          this.order.mountingType = this.order.mountingType ?? MountingType.bottom; // Default mounting
          this.order.depth = this.order.depth ?? 2000; // Example default
          this.order.depthRight = this.order.depthRight ?? 2000; // Example default
          this.order.calfCircumferenceWithoutDeduction = this.order.calfCircumferenceWithoutDeduction ?? 2000; // Example default
          this.order.heightWithoutDeduction = this.order.heightWithoutDeduction ?? 1100; // Example default
      }
    } catch (error) {
        console.error('Error mounting fence component:', error);
    }
  }

  @Emit('isValid')
  async validateForm(): Promise<boolean> {
    if (!this.formRef) {
      console.warn('Form ref not available for validation (fence).');
      return false;
    }
    try {
      // Validate the form - ensure Element Plus form validation rules are set up on items if needed
      const valid = await this.formRef.validate();
      return valid;
    } catch (error) {
      // Catch potential validation errors (e.g., promise rejection on failure)
      // Element Plus validate usually rejects on validation failure
      console.warn('Form validation failed (fence):', error);
      return false;
    }
  }

  getImageSrc(type: string) {
    const images = import.meta.glob('@/assets/images/balcony/*.png', { eager: true });
    return (images[`/src/assets/images/balcony/${type}.png`] as any)?.default || '';
  }
}
</script>

<style scoped lang="scss">
/* Add your original styles here */
.flex-container {
  display: flex;
  flex-wrap: wrap; /* Allow items to wrap */
  gap: 20px; /* Add some space between items */
  margin-bottom: 20px; /* Space below the top section */
}

/* Ensure form items have reasonable width or allow flexbox to manage them */
.el-form-item {
   flex-grow: 1; /* Allow items to grow */
   min-width: 200px; /* Prevent items from becoming too small */
}

.select-component,
.number-input-component,
.el-slider {
  width: 100%; /* Make inputs/sliders take full width of form item */
}

.radio-Button .el-radio-button__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 15px; /* Adjust padding if needed */
}

.option-label {
  display: flex;
  align-items: center;
  justify-content: space-between; /* Pushes image to the right */
  width: 100%;
}

.option-image {
  width: 45px;
  height: auto; /* Maintain aspect ratio */
  margin-left: 8px;
}

.parameter-img {
  padding-top: 0; /* Removed padding-top as tooltip content usually positions well */
  width: 100px;
  height: 100px;
  display: block; /* Ensure image behaves as block */
  margin: 0 auto; /* Center image if needed */
}

.text-secondary {
  color: #0000008d !important;
}

/* Add other styles from your original component */
.disabled-hover .radio-label {
  transition: color 0.3s ease-in-out;
  /* ... other disabled styles */
}

/* Style fixes for v-show visibility */
.el-form-item[style*="display: none;"] {
  margin-bottom: 0; /* Avoid extra margin when hidden */
}
</style>